import {
  CUSTOM_ELEMENTS_SCHEMA,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { RangeModule } from '@purespectrum1/ui/range';
import { TabsModule } from '@purespectrum1/ui/tabs';
import { ModalModule } from '@purespectrum1/ui/modal';
import { SliderModule } from '@purespectrum1/ui/slider';
import { ButtonModule } from '@purespectrum1/ui/button';
import { TooltipModule } from '@purespectrum1/ui/tooltip';
import { AccordionModule } from '@purespectrum1/ui/accordion';
import { SelectDropdownModule } from '@purespectrum1/ui/select-dropdown';
import { DropdownModule } from '@purespectrum1/ui/dropdown';
import { ToasterModule } from '@purespectrum1/ui/toaster-service';
import { LoaderModule } from '@purespectrum1/ui/loader';
import {
  GoogleTagManagerModule,
  HotjarModule,
} from '@purespectrum1/ui/tracking';

import { GtagConfig } from '../shared/interfaces/tracking-service.interface';
import {
  CreateSurveyDataComponent,
  FooterComponent,
  HeaderComponent,
  HeaderServiceAccountComponent,
  LayoutModule,
} from './layout/layout.module';
import { ConfigEnabledModule } from './shared/ui/config-enabled/config-enabled.module';

import { InputRestrictionModule } from './shared/ui/input-restriction/input-restriction.module';

import { CreateSurveysComponent } from './create-surveys.component';
import { MultiselectInputButtonComponent } from './multiselect-input-button/multiselect-input-button.component';
import { SearchLocationQualificationComponent } from './search-location-qualification/search-location-qualification.component';
import { RangeInputButtonComponent } from './range-input-button/range-input-button.component';
import { QuotaModalComponent } from './quota-modal/quota-modal.component';
import { RangeQuotaRowComponent } from './quota-modal/range-quota-row/range-quota-row.component';
import { MultiselectQuotaRowComponent } from './quota-modal/multiselect-quota-row/multiselect-quota-row.component';
import { SelectboxLocationQualificationComponent } from './selectbox-location-qualification/selectbox-location-qualification.component';
import { UploadLocationQualificationComponent } from './upload-location-qualification/upload-location-qualification.component';
import { ZipListTooLargeModalComponent } from './upload-location-qualification/zip-list-too-large-modal/zip-list-too-large-modal.component';
import { ChildAgeGenderRowComponent } from './quota-modal/child-age-gender-quota-row/child-age-gender-row.component';
import { AdvanceTargetingQualificationComponent } from './advance-targeting-qualification/advance-targeting-qualification.component';
import { DraftSurveyConfirmationModalComponent } from './draft-survey-confirmation-modal/draft-survey-confirmation-modal.component';
import { QuotaNestingModalComponent } from './quota-nesting/quota-nesting-modal.component';
import { MultiCountryModule } from './shared/ui/multicountry/multi-country.module';
import { PurePriceWindowComponent } from './pure-price-window/pure-price-window.component';
import { AdvanceTargetingQuotasComponent } from './advance-targeting-qualification/advance-targeting-quotas/advance-targeting-quotas.component';
import { GroupQuotaComponent } from './quota-modal/group-quota/group-quota.component';
import { CapitalizePipeModule } from './shared/pipes/capitalize-pipe.module';
import { RightSliderWindowComponent } from './right-slider-window/right-slider-window.component';
import { ConfirmationModalModule } from '../shared/ui/confirmation-modal/confirmation-modal.module';
import { SupplierCtUpdateWarningModalComponent } from './shared/ui/supplier-ct-update-warning-modal/supplier-ct-update-warning-modal.component';
import { EditSurveyConfirmationModalComponent } from './shared/ui/edit-survey-confirmation-modal/edit-survey-confirmation-modal.component';
import { AdvanceTargetingModalComponent } from './advance-targeting-qualification/advance-targeting-modal/advance-targeting-modal.component';
import { EnvironmentConfig } from '../shared/interfaces/environment.interface';
import { NgSelectModule } from '@ng-select/ng-select';
import { TreeDropdownModule } from './shared/ui/tree-dropdown/tree-dropdown.module';
import { MarginRangeModule } from './shared/ui/margin-range/margin-range.module';
import { EpcComponent } from './epc/epc.component';
import { QuotaGroupingModalComponent } from './quota-grouping-modal/quota-grouping-modal.component';
import { RecontactSurveyComponent } from './recontact-survey/recontact-survey.component';
import { PsidViewModalComponent } from './recontact-survey/psid-view-modal/psid-view-modal.component';
import { BillingNumberTooltipModule } from '../shared/ui/billing-number-tooltip/billing-number-tooltip.module';
import { TableModule } from '@purespectrum1/ui/table';
import { ActivityLogModule } from '../shared/directives/activity-log/activity-log.module';
import { CreateSurveyInfoHeaderModule } from './layout/header/create-survey-info-header/create-survey-info-header.module';
import { MultiQuotaNestingModalModule } from './multi-quota-nesting-modal/multi-quota-nesting-modal.module';

export * from './marketplace.service';
export * from './layout/layout.module';
export * from './create-surveys.component';

const gTagConfig: GtagConfig = {
  trackingId: '',
  trackHotjarGTM: false,
  trackPageviews: false,
  enableHotjarEvent: false,
};

@NgModule({
  declarations: [
    CreateSurveysComponent,
    MultiselectInputButtonComponent,
    SearchLocationQualificationComponent,
    RangeInputButtonComponent,
    QuotaModalComponent,
    RangeQuotaRowComponent,
    MultiselectQuotaRowComponent,
    ChildAgeGenderRowComponent,
    SelectboxLocationQualificationComponent,
    UploadLocationQualificationComponent,
    ZipListTooLargeModalComponent,
    AdvanceTargetingQualificationComponent,
    DraftSurveyConfirmationModalComponent,
    QuotaNestingModalComponent,
    PurePriceWindowComponent,
    AdvanceTargetingQuotasComponent,
    GroupQuotaComponent,
    RightSliderWindowComponent,
    SupplierCtUpdateWarningModalComponent,
    EditSurveyConfirmationModalComponent,
    EpcComponent,
    AdvanceTargetingModalComponent,
    QuotaGroupingModalComponent,
    RecontactSurveyComponent,
    PsidViewModalComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    ButtonModule,
    SelectDropdownModule,
    DropdownModule,
    RangeModule,
    ModalModule,
    MultiCountryModule,
    AccordionModule,
    ConfigEnabledModule,
    TabsModule,
    LoaderModule.forRoot({
      loaderUrls: ['com', 'localhost'],
      excludeUrls: [
        // create surveys
        '/v2/supplier-blends',
        '/supplier/getFlexibilityPricesParams',
        '/feasibility',
        '/v1/pricing-reco',
        '/v1/calculate-permutations',
        '/countries',
        '/v2/similar-projects',
      ],
    }),
    SliderModule,
    LayoutModule,
    InputRestrictionModule,
    CapitalizePipeModule,
    TooltipModule,
    ConfirmationModalModule,
    ToasterModule,
    NgSelectModule,
    TreeDropdownModule,
    MarginRangeModule,
    GoogleTagManagerModule.forRoot(gTagConfig),
    HotjarModule,
    BillingNumberTooltipModule,
    TableModule,
    ActivityLogModule,
    CreateSurveyInfoHeaderModule,
    MultiQuotaNestingModalModule,
  ],
  exports: [
    CreateSurveysComponent,
    HeaderComponent,
    FooterComponent,
    RightSliderWindowComponent,
    RecontactSurveyComponent,
    CreateSurveyDataComponent,
    HeaderServiceAccountComponent,
  ],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateSurveyModule {
  static forRoot(
    config: EnvironmentConfig
  ): ModuleWithProviders<CreateSurveyModule> {
    return getModuleSettings(config);
  }
  static forChild(
    config: EnvironmentConfig
  ): ModuleWithProviders<CreateSurveyModule> {
    return getModuleSettings(config);
  }
}

const getModuleSettings = (config: EnvironmentConfig) => {
  return {
    ngModule: CreateSurveyModule,
    providers: [
      {
        provide: 'ENV',
        useValue: config || {},
      },
    ],
  };
};
